import { io, Socket } from "socket.io-client";

import { type InventoryValueEntry, reapOrphanTemps } from "@/domain/inventory/hooks/api/apply-optimistic-upsert";
import { useCommandQueueStore } from "@/domain/inventory/lib/command-queue/command-queue-store";
import type { InventoryTotals } from "@ark/shared";
import type { QueryClient } from "@tanstack/react-query";

import { queryKeys } from "./react-query.service/query-keys";
import { serializeConnectionError } from "./serialize-connection-error";
import { getAccessToken } from "./storage.service";
import { getWebSocketUrl } from "./websocket-url";

export interface InventoryUpdatedPayload {
  inventoryId: string;
  triggeredBy: string;
  timestamp: string;
  updates?: Array<{
    id: string;
    inventoryId: string;
    energySourceId?: string | null;
    streetType?: string | null;
    sectorId: string;
    subsectorId?: string | null;
    subsubsectorId?: string | null;
    sectorSliceId?: string | null;
    type: string;
    amount: number;
    quality?: number | null;
    dataSource?: string | null;
    displayedPrecision?: number | null;
    createdAt: string;
    updatedAt: string;
  }>;
  deletedIds?: string[];
  totals?: InventoryTotals | null;
  idMap?: Record<string, string>; // temp-id -> real-id mapping
}

export interface InventoryErrorPayload {
  inventoryId: string;
  error: string;
  attempts: number;
  timestamp: string;
}

const INVENTORY_FALLBACK_POLL_INTERVAL_MS = 30_000;

class InventoryWebSocketService {
  private socket: Socket | null = null;
  private currentWorkspaceId: string | null = null;
  private _currentInventoryId: string | null = null; // Rooms are per inventory
  private queryClient: QueryClient | null = null;
  private pollingInterval: ReturnType<typeof setInterval> | null = null;
  private isConnecting = false;
  private lastInvalidationTime: Map<string, number> = new Map(); // Track last invalidation time per inventory ID
  private fallbackTimeouts: Map<string, ReturnType<typeof setTimeout>> = new Map(); // Track fallback timeouts per inventory ID

  setQueryClient(queryClient: QueryClient): void {
    this.queryClient = queryClient;
  }

  trackInvalidation(inventoryId: string): void {
    // Track invalidation time so handleInventoryUpdate can debounce
    this.lastInvalidationTime.set(inventoryId, Date.now());
  }

  registerFallbackTimeout(inventoryId: string, timeout: ReturnType<typeof setTimeout>): void {
    // Cancel any existing timeout for this inventory
    this.cancelFallbackTimeout(inventoryId);
    // Register the new timeout
    this.fallbackTimeouts.set(inventoryId, timeout);
  }

  cancelFallbackTimeout(inventoryId: string): void {
    const existingTimeout = this.fallbackTimeouts.get(inventoryId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      this.fallbackTimeouts.delete(inventoryId);
    }
  }

  private getQueryClient(): QueryClient | null {
    // Return the query client set by the hook
    // Each tab has its own instance of this service, so each tab maintains its own query client
    return this.queryClient;
  }

  connect(workspaceId: string, inventoryId?: string | null): void {
    // Require inventoryId to join inventory-specific room
    if (!inventoryId) {
      console.warn("Cannot connect to WebSocket: inventoryId is required");
      return;
    }

    // Connect to inventory room if not already connected to this inventory
    if (this.socket && this.socket.connected && this._currentInventoryId === inventoryId) {
      return; // Already connected to this inventory
    }

    this.disconnect();

    const token = getAccessToken();
    if (!token) {
      console.warn("No access token available for WebSocket connection");
      return;
    }

    const websocketUrl = getWebSocketUrl();
    if (!websocketUrl) {
      console.warn("WebSocket URL not configured");
      return;
    }

    this.isConnecting = true;
    console.log(`Connecting to WebSocket: ${websocketUrl}/inventory`);

    this.socket = io(`${websocketUrl}/inventory`, {
      path: "/socket.io",
      auth: {
        token,
      },
      autoConnect: false,
      // Prefer websocket for real-time updates, fallback to polling only if websocket fails
      transports: ["websocket", "polling"],
      // Aggressive reconnection to maintain connection
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity, // Keep trying to reconnect indefinitely
      timeout: 20000,
      // Force new connection to avoid stale connections
      forceNew: false,
      // Upgrade from polling to websocket if available
      upgrade: true,
    });

    this.currentWorkspaceId = workspaceId;
    this._currentInventoryId = inventoryId;

    this.socket.on("connect", () => {
      this.isConnecting = false;
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }

      const transport = this.socket?.io?.engine?.transport?.name || "unknown";
      if (this._currentInventoryId === inventoryId) {
        console.log(`WebSocket connected for inventory ${inventoryId} [transport: ${transport}]`);
      }
    });

    // Joining the room is gated on `authenticated`, which is the only event that may trigger join-inventory.
    this.socket.on("authenticated", () => {
      if (this._currentInventoryId === inventoryId && this.socket?.connected) {
        console.log(`WebSocket authenticated for inventory ${inventoryId}`);
        this.socket.emit("join-inventory", { inventoryId });
      }
    });

    this.socket.on("joined-inventory", (payload: { inventoryId: string }) => {
      if (this._currentInventoryId === payload.inventoryId) {
        console.log(`✅ Successfully joined inventory room: ${payload.inventoryId}`);
        console.log(`WebSocket transport: ${this.getTransport()}, connected: ${this.isConnected()}`);
      }
    });

    this.socket.on("reconnect", (attemptNumber: number) => {
      console.log(`WebSocket reconnected after ${attemptNumber} attempts`);
    });

    this.socket.on("error", (error: { message: string }) => {
      console.error(`WebSocket error for workspace ${workspaceId}:`, error.message);
    });

    this.socket.on("inventory-updated", (payload: InventoryUpdatedPayload) => {
      // Handle inventory updates for any inventory in the workspace
      console.log(
        `🔔 Received inventory-updated event for ${payload.inventoryId} (triggered by: ${payload.triggeredBy})${this._currentInventoryId === payload.inventoryId ? " [current inventory]" : ""} | Updates: ${payload.updates?.length || 0}, Deletions: ${payload.deletedIds?.length || 0}${payload.timestamp ? ` | Timestamp: ${payload.timestamp}` : ""}`,
      );
      console.log(
        `WebSocket state: transport=${this.getTransport()}, connected=${this.isConnected()}, currentInventory=${this._currentInventoryId}`,
      );
      this.handleInventoryUpdate(payload);
    });

    this.socket.on("inventory-error", (payload: InventoryErrorPayload) => {
      // Handle inventory errors for any inventory in the workspace
      this.handleInventoryError(payload);
    });

    this.socket.on("connect_error", (error) => {
      if (!this.isConnecting && this.currentWorkspaceId !== workspaceId) {
        return;
      }
      // Recoverable transport failure: socket.io keeps retrying and we start
      // polling as a fallback below, so this is degraded operation, not a hard
      // error. Warn level keeps it out of the error-level RUM stream.
      console.warn(`WebSocket connection error: ${serializeConnectionError(error)}`);
      if (this.currentWorkspaceId === workspaceId) {
        // Start polling as fallback only if websocket completely fails
        // Socket.io will keep trying to reconnect, so polling is just a safety net
        this.startPolling();
      }
    });

    this.socket.on("disconnect", (reason: string) => {
      this.isConnecting = false;
      console.warn(`WebSocket disconnected: ${reason}`);
      // Don't clear polling interval on disconnect - let reconnection handle it
      // Only start polling if reconnection fails (handled in connect_error)
    });

    this.socket.connect();
  }

  isConnected(): boolean {
    return this.socket?.connected === true;
  }

  getTransport(): string | null {
    return this.socket?.io?.engine?.transport?.name || null;
  }

  disconnect(): void {
    if (this.socket) {
      try {
        this.socket.removeAllListeners();
        if (this.socket.connected) {
          this.socket.disconnect();
        }
      } catch (error) {
        console.warn("Error during WebSocket disconnect (expected in development):", error);
      }
      this.socket = null;
    }
    this.currentWorkspaceId = null;
    this._currentInventoryId = null;
    this.isConnecting = false;
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  private handleInventoryUpdate(payload: InventoryUpdatedPayload): void {
    const queryClient = this.getQueryClient();
    if (!queryClient) {
      console.warn("No query client available for inventory update");
      return;
    }

    const { inventoryId, updates, deletedIds, totals, idMap, timestamp } = payload;

    // Get workspaceId from the inventory to invalidate the list query
    const inventory = queryClient.getQueryData<{
      workspaceId?: string;
      inventoryValueList?: Array<{ id: string; [key: string]: unknown }>;
      updatedAt?: string;
      [key: string]: unknown;
    }>(queryKeys.inventories.detail(inventoryId));
    const workspaceId = inventory?.workspaceId || this.currentWorkspaceId;

    // Cancel any pending fallback timeout since we received a WebSocket update
    this.cancelFallbackTimeout(inventoryId);

    // Map temp-ids in deletedIds to real-ids before processing
    // This ensures deletions work correctly even when values were created with temp-ids
    const mappedDeletedIds =
      idMap && Object.keys(idMap).length > 0 && deletedIds ? deletedIds.map((id) => idMap[id] || id) : deletedIds || [];

    // === SURGICAL CACHE PATCHING ===
    // We use setQueryData to manually update the main inventory cache.
    // This is the core of the performance improvement.
    // The updater function ensures atomic updates even with rapid sequential updates.
    // React Query queues setQueryData calls and applies them sequentially, so concurrent
    // updates are handled correctly - each update sees the result of the previous one.

    // Check if we have cached data
    const cachedInventory = queryClient.getQueryData<typeof inventory>(queryKeys.inventories.detail(inventoryId));
    const queryState = queryClient.getQueryState(queryKeys.inventories.detail(inventoryId));
    const isQueryActive = queryState && queryState.status === "success";

    // If we have updates/deletions and no cache, check if query is active and refetch/invalidate
    // But continue execution to process temp deletes, clean up individual values, and invalidate lists
    if (!cachedInventory && ((updates && updates.length > 0) || mappedDeletedIds.length > 0)) {
      if (isQueryActive) {
        // Query is active but cache is empty - refetch to get the complete inventory
        console.log(`Refetching inventory ${inventoryId} - query is active but cache is empty`);
        queryClient.refetchQueries({
          queryKey: queryKeys.inventories.detail(inventoryId),
          type: "active",
        });
      } else {
        // Query not active, invalidate so it refetches when it becomes active
        queryClient.invalidateQueries({
          queryKey: queryKeys.inventories.detail(inventoryId),
          refetchType: "active",
        });
      }
      // Continue execution - don't return early so we can still:
      // - Process pending temp deletes
      // - Clean up individual inventoryValues caches
      // - Invalidate workspace lists
    }

    // Only apply surgical updates if we have cache data
    // If cache is missing, we've already triggered refetch/invalidate above
    if (cachedInventory) {
      // If we have cache data, always apply surgical updates (even if query isn't currently active)
      // This ensures the cache is up-to-date when the user navigates to the inventory
      queryClient.setQueryData(queryKeys.inventories.detail(inventoryId), (oldData: typeof inventory | undefined) => {
        // If the cache is empty (shouldn't happen after the check above, but safety check)
        if (!oldData) {
          queryClient.invalidateQueries({
            queryKey: queryKeys.inventories.detail(inventoryId),
            refetchType: "active",
          });
          return undefined;
        }

        // Check if this update is newer than what's in the cache (using timestamp from payload)
        // This prevents applying stale updates if messages arrive out of order
        // This is critical for multi-server setups where events might arrive in different orders
        if (timestamp && oldData.updatedAt) {
          const payloadTime = new Date(timestamp).getTime();
          const cacheTime = new Date(oldData.updatedAt).getTime();
          if (payloadTime < cacheTime) {
            console.warn(
              `⏭️ Skipping stale WebSocket update for ${inventoryId}: payload timestamp ${timestamp} (${payloadTime}) is older than cache timestamp ${oldData.updatedAt} (${cacheTime}). This is expected if events arrive out of order.`,
            );
            return oldData; // Don't apply stale update - keep the newer data
          }
        }

        let newValues = [...(oldData.inventoryValueList || [])];

        // 1. Use idMap to replace temp-ids with real-ids BEFORE processing updates/deletions
        if (idMap && Object.keys(idMap).length > 0) {
          // Capture the set of existing IDs to prevent duplicates
          const existingIdSet = new Set(newValues.map((v) => v.id));
          // Track resolved temp-ids to avoid re-processing
          const resolvedTempIdSet = new Set<string>();

          newValues = newValues.map((value) => {
            const realId = idMap[value.id]; // Check if this temp-id was mapped

            // Only perform replacement if:
            // 1. value.id is a key in idMap
            // 2. The mapped realId is not already present in the existing-id set
            // 3. value.id doesn't already equal the realId (skip unnecessary replacement)
            // 4. This temp-id hasn't been resolved yet
            if (realId && !existingIdSet.has(realId) && value.id !== realId && !resolvedTempIdSet.has(value.id)) {
              // Update the existing-id set to track the new realId
              existingIdSet.add(realId);
              // Track that this temp-id has been resolved
              resolvedTempIdSet.add(value.id);
              return { ...value, id: realId }; // Swap temp-id for real-id
            }
            return value;
          });
        }

        // 2a. Handle Deletions
        // Use mapped deletedIds (with temp-ids converted to real-ids) to ensure deletions work correctly
        if (mappedDeletedIds.length > 0) {
          const deletedIdSet = new Set(mappedDeletedIds);
          const beforeCount = newValues.length;
          newValues = newValues.filter((v) => !deletedIdSet.has(v.id));
          const afterCount = newValues.length;
          const actuallyDeleted = beforeCount - afterCount;
          if (actuallyDeleted !== mappedDeletedIds.length) {
            console.warn(
              `⚠️ Deletion mismatch: Received ${mappedDeletedIds.length} deleted IDs but only removed ${actuallyDeleted} values from cache (before: ${beforeCount}, after: ${afterCount})`,
            );
            console.warn(
              `Deleted IDs: ${mappedDeletedIds.slice(0, 10).join(", ")}${mappedDeletedIds.length > 10 ? `... (${mappedDeletedIds.length - 10} more)` : ""}`,
            );
          } else {
            console.log(`✅ Successfully deleted ${actuallyDeleted} values from cache`);
          }
        }

        // 2b. Handle Updates & Creates
        // Process all updates to ensure none are missed
        if (updates && updates.length > 0) {
          const processedUpdateIds = new Set<string>();
          updates.forEach((update) => {
            // Re-hydrate the date strings from the WebSocket payload
            const hydratedUpdate = {
              ...update,
              createdAt: new Date(update.createdAt),
              updatedAt: new Date(update.updatedAt),
            };

            const index = newValues.findIndex((v) => v.id === update.id);

            if (index !== -1) {
              // UPDATE: Overwrite the existing value with complete object from backend
              newValues[index] = hydratedUpdate as (typeof newValues)[0];
              processedUpdateIds.add(update.id);
            } else {
              // CREATE: Add the new value
              newValues.push(hydratedUpdate as (typeof newValues)[0]);
              processedUpdateIds.add(update.id);
            }
          });

          // Validate that all updates were processed
          if (processedUpdateIds.size !== updates.length) {
            console.warn(
              `Warning: Processed ${processedUpdateIds.size} updates but received ${updates.length} for inventory ${inventoryId}. Some updates may have been skipped.`,
            );
          }

          // 2c. Reap orphan temp-ids whose cell coordinates match a real-id update.
          // See reapOrphanTemps for full rationale; without this the optimistic
          // temp pushed by a CREATE-without-tempId stays in cache forever and
          // shadows the canonical real entry for any cellStore.find() consumer.
          //
          // The cache element type is loose (`[key: string]: unknown`) for
          // historical reasons, but at runtime each entry carries the cell-coord
          // fields that `reapOrphanTemps` reads — narrowing here is a no-op cast.
          newValues = reapOrphanTemps<InventoryValueEntry>(
            newValues as ReadonlyArray<InventoryValueEntry>,
            updates as ReadonlyArray<InventoryValueEntry>,
          ) as typeof newValues;
        }

        // Return the new state for React Query to cache
        // Use the timestamp from the payload to ensure we track the actual server update time
        // This helps with ordering and prevents applying stale updates
        const newUpdatedAt = timestamp || new Date().toISOString();
        return {
          ...oldData,
          inventoryValueList: newValues,
          ...(totals
            ? {
                totalEnergyConsumption: totals.totalEnergyConsumption,
                totalEmissions: totals.totalEmissions,
                totalDataQuality: totals.totalDataQuality,
              }
            : {}),
          updatedAt: newUpdatedAt,
        };
      });
    }

    // Log update processing for debugging
    console.log(
      `Processed WebSocket update for ${inventoryId}: ${updates?.length || 0} updates, ${mappedDeletedIds.length} deletions${timestamp ? ` (timestamp: ${timestamp})` : ""} | Cache exists: ${!!cachedInventory}, Query active: ${isQueryActive}`,
    );

    // Note: We don't refetch here because:
    // 1. setQueryData automatically triggers re-renders for active queries
    // 2. Refetching immediately after surgical updates can cause race conditions
    // 3. Refetching can overwrite surgical updates if multiple events arrive quickly
    // 4. The surgical cache update is atomic and immediate, which is better UX
    // If the surgical update fails or data is inconsistent, React Query will handle it
    // through its normal stale/refetch mechanisms

    // Update computed totals in the cache directly instead of invalidating
    // This avoids refetches that cause delays and ensures immediate updates
    // The computed totals (totalEnergyConsumption, totalEmissions, totalDataQuality)
    // are recalculated on the backend and included in the inventory object when refetched,
    // but for real-time updates, we rely on the surgical cache update above which is immediate.
    // If computed totals are critical, they should be included in the WebSocket payload.
    // For now, we skip invalidation to ensure smooth, immediate updates without delays.

    // Process pending temp-id deletes using idMap (much more reliable than fuzzy matching)
    useCommandQueueStore.getState().processPendingTempDeletesWithIdMap(inventoryId, idMap || {});

    // === INDIVIDUAL QUERY UPDATES ===
    // This is still a good practice for components that
    // might only be subscribed to a single value.

    // 3a. Update individual queries for changed values
    updates?.forEach((update) => {
      queryClient.setQueryData(queryKeys.inventoryValues.detail(update.id), {
        ...update,
        createdAt: new Date(update.createdAt),
        updatedAt: new Date(update.updatedAt),
      });
    });

    // 3b. Remove individual queries for deleted values
    // Use mappedDeletedIds to ensure temp-ids are properly handled
    mappedDeletedIds?.forEach((id) => {
      queryClient.removeQueries({
        queryKey: queryKeys.inventoryValues.detail(id),
      });
    });

    // === LIST INVALIDATION ===
    // We still invalidate the *list* query. This is CHEAP.
    // It just re-fetches the list (e.g., names, totals, status),
    // not the massive inventory data itself. This is correct.
    if (workspaceId) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.inventories.list(workspaceId, true),
        refetchType: "active",
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.inventories.list(workspaceId, false),
        refetchType: "active",
      });
    }
  }

  private handleInventoryError(payload: InventoryErrorPayload): void {
    console.error(`Inventory computation error for ${payload.inventoryId}:`, payload.error);
    const queryClient = this.getQueryClient();
    if (queryClient) {
      // Check if query exists before invalidating
      const queryState = queryClient.getQueryState(queryKeys.inventories.detail(payload.inventoryId));
      if (queryState && queryState.data) {
        // Only refetch if query exists and has data
        queryClient.refetchQueries({
          queryKey: queryKeys.inventories.detail(payload.inventoryId),
          type: "active",
        });
      }
    }
  }

  private startPolling(): void {
    if (this.pollingInterval) {
      return;
    }

    this.pollingInterval = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      const queryClient = this.getQueryClient();
      if (queryClient) {
        queryClient.refetchQueries({ queryKey: queryKeys.inventories.all, type: "active" }, { cancelRefetch: false });
      }
    }, INVENTORY_FALLBACK_POLL_INTERVAL_MS);
  }
}

export const inventoryWebSocket = new InventoryWebSocketService();
